import type { ReactNode } from 'react'

interface PageHeaderProps {
  /** Dynamic content title — selected entity. NEVER repeat the nav label. */
  title: string
  /** One-line context: ID, parent, last-updated. */
  subtitle?: ReactNode
  /** Render only when depth ≥ 2 (excludes current title). */
  breadcrumb?: string[]
  /** Right-aligned action buttons. */
  actions?: ReactNode
  /** Inline pills next to the title. */
  badges?: ReactNode
  /** Below 640 px the actions sit under the title instead of squeezing it (default: beside it, as before). */
  stackActionsOnPhone?: boolean
  /** Small primary-coloured line above the title (the section, e.g. "Personal"). Opt-in; DESIGN.md §8b: only where it adds context. */
  eyebrow?: string
  /** The big title look of the glass header prototype: 900 weight, 34 px (26 px on a phone), not cut off; the row wraps (title basis 320 px). Opt-in. */
  display?: boolean
  /** Between the title and the actions, e.g. the avatars of the users working on the page. */
  aside?: ReactNode
}

/**
 * Single source of truth for page headers. See DESIGN.md § 9 + § 14.
 * Renders as a Liquid Glass band framing the page H1 — chrome layer, not data.
 */
export function PageHeader({ title, subtitle, breadcrumb, actions, badges, stackActionsOnPhone = false, eyebrow, display = false, aside }: PageHeaderProps) {
  const showCrumb = breadcrumb && breadcrumb.length >= 2
  return (
    <header className="liquid-glass rounded-3xl px-5 py-4 mb-6">
      {showCrumb && (
        <nav className="flex items-center gap-1.5 text-[11px] text-on-surface-variant mb-2" aria-label="Breadcrumb">
          {breadcrumb!.map((c, i) => (
            <span key={i} className="flex items-center gap-1.5">
              {i > 0 && <span className="text-on-surface-variant/40">/</span>}
              <span className={i === breadcrumb!.length - 1 ? 'text-on-surface font-medium' : ''}>{c}</span>
            </span>
          ))}
        </nav>
      )}
      {/* display (the glass header prototype): the row wraps and the title block has a basis of 320 px, so avatars and actions drop below the
          title instead of squeezing it into a column (Tablet mockup: flex-wrap, title flex 1 1 320px). All other headers stay as they were. */}
      <div className={`flex ${display ? 'flex-wrap items-center gap-x-4 gap-y-3' : 'items-start justify-between gap-6'}${stackActionsOnPhone ? ' max-sm:flex-col max-sm:gap-3' : ''}`}>
        <div className={`min-w-0 ${display ? 'max-sm:flex-1 sm:flex-[1_1_320px]' : 'flex-1'}`}>
          {eyebrow && <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-primary sm:text-[13px]">{eyebrow}</div>}
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1
              className={display
                ? 'font-headline font-black text-[26px] sm:text-[34px] leading-[1.1] text-on-surface tracking-[-0.01em] max-w-full'
                : 'font-headline font-bold text-2xl text-on-surface tracking-tight truncate max-w-full'}
              title={title}
            >{title}</h1>
            {badges}
          </div>
          {subtitle && <p className="text-sm text-on-surface-variant mt-1">{subtitle}</p>}
        </div>
        {aside}
        {actions && <div className={`flex items-center gap-2 flex-shrink-0${stackActionsOnPhone ? ' max-sm:flex-wrap' : ''}`}>{actions}</div>}
      </div>
    </header>
  )
}
